import {
  ChangeEvent,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DataContext from "../../../context/DataContext";
import { useToolsRunTrackedWells } from "../../../hooks";
import {
  ToolsRunLineItemProps,
  ToolsRunUploadSummaryProps,
} from "../../../interfaces/interfaces";
import {
  ToolsRunDraftOverride,
  ToolsRunDraftResolveDialog,
} from "./ToolsRunDraftResolveDialog";
import { ToolsRunStatusBadge } from "./ToolsRunStatusBadge";

type NamedOption = { id: number; name: string };
type ProductOption = NamedOption & { partNumber?: string };
type BusyAction = "preview" | "confirm" | null;

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: (result: ToolsRunUploadSummaryProps) => void;
}

const metricColors = ["#314157", "#237A57", "#9A6200", "#B43A3A", "#69717D"];

const reasonLabel = (value?: string | null) =>
  value
    ? value
        .split(",")
        .map((reason) => reason.replaceAll("_", " "))
        .join(" · ")
    : "";

export const ToolsRunUploadForm = ({ open, onClose, onUploaded }: Props) => {
  const {
    clientOptions,
    productOptions,
  } = useContext(DataContext);
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const clients = clientOptions as NamedOption[];
  const wells = wellOptions as NamedOption[];
  const products = productOptions as ProductOption[];
  const abortRef = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [client, setClient] = useState<NamedOption | null>(null);
  const [draft, setDraft] = useState<ToolsRunUploadSummaryProps | null>(null);
  const [overrides, setOverrides] = useState<ToolsRunDraftOverride[]>([]);
  const [editingRow, setEditingRow] =
    useState<ToolsRunLineItemProps | null>(null);
  const [busy, setBusy] = useState<BusyAction>(null);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "" | "matched" | "needs_review" | "error" | "skipped"
  >("");

  const summary = useMemo(() => {
    if (!draft) return null;
    return {
      ...draft.summary,
      matched: draft.rows.filter((row) => row.status === "matched").length,
      needsReview: draft.rows.filter((row) => row.status === "needs_review")
        .length,
      error: draft.rows.filter((row) => row.status === "error").length,
    };
  }, [draft]);

  const visibleRows = useMemo(() => {
    if (!draft) return [];
    if (statusFilter === "skipped") return [];
    if (!statusFilter) return draft.rows;
    return draft.rows.filter((row) => row.status === statusFilter);
  }, [draft, statusFilter]);

  const emptyStateMessage = useMemo(() => {
    if (statusFilter === "skipped") {
      return `${summary?.skippedDuplicate || 0} row(s) were skipped (duplicates or zero quantity) — nothing to display.`;
    }
    if (statusFilter === "matched") return "No matched rows.";
    if (statusFilter === "needs_review") return "No rows need review.";
    if (statusFilter === "error") return "No rows with errors.";
    return "No rows in this workbook.";
  }, [statusFilter, summary]);

  // Breaks down *why* rows errored (e.g. "date invalid: 21,888") so a
  // whole-file failure is diagnosable without opening each row individually.
  const errorReasonBreakdown = useMemo(() => {
    if (!draft) return [];
    const counts = new Map<string, number>();
    draft.rows
      .filter((row) => row.status === "error")
      .forEach((row) => {
        const reason = row.errorReason || "unknown";
        counts.set(reason, (counts.get(reason) || 0) + 1);
      });
    return [...counts.entries()].sort((left, right) => right[1] - left[1]);
  }, [draft]);

  const reset = () => {
    setFile(null);
    setClient(null);
    setDraft(null);
    setOverrides([]);
    setEditingRow(null);
    setBusy(null);
    setError("");
    setStatusFilter("");
    abortRef.current = null;
  };

  const cancel = () => {
    abortRef.current?.abort();
    reset();
    onClose();
  };

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    setError("");
    setDraft(null);
    setOverrides([]);
    setFile(event.target.files?.[0] || null);
  };

  const chooseClient = (value: NamedOption | null) => {
    setClient(value);
    setDraft(null);
    setOverrides([]);
    setError("");
    if (value) fetchDataWells(value.id);
  };

  const postWorkbook = async (
    endpoint: string,
    extra?: Record<string, string>,
  ) => {
    if (!file || !client) throw new Error("Select a client and workbook");
    const controller = new AbortController();
    abortRef.current = controller;
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("clientId", String(client.id));
    Object.entries(extra || {}).forEach(([key, value]) =>
      formData.append(key, value),
    );
    const response = await fetch(
      String(process.env.REACT_APP_DEV_API) + endpoint,
      {
        method: "POST",
        body: formData,
        signal: controller.signal,
        headers: { Authorization: "Bearer " + localStorage.getItem("info") },
      },
    );
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || "The workbook could not be processed");
    }
    return result.data as ToolsRunUploadSummaryProps;
  };

  const preview = async () => {
    setBusy("preview");
    setError("");
    try {
      const result = await postWorkbook("/tools-run-preview");
      setDraft(result);
    } catch (requestError) {
      if (requestError instanceof DOMException && requestError.name === "AbortError")
        return;
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The workbook could not be previewed",
      );
    } finally {
      setBusy(null);
      abortRef.current = null;
    }
  };

  const confirm = async () => {
    setBusy("confirm");
    setError("");
    try {
      const result = await postWorkbook("/tools-run-upload", {
        overrides: JSON.stringify(overrides),
      });
      reset();
      onClose();
      onUploaded(result);
    } catch (requestError) {
      if (requestError instanceof DOMException && requestError.name === "AbortError")
        return;
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The workbook could not be imported",
      );
    } finally {
      setBusy(null);
      abortRef.current = null;
    }
  };

  const norm = (value?: string | null) => (value || "").trim().toLowerCase();

  const saveDraftRow = (
    override: ToolsRunDraftOverride,
    updatedRow: ToolsRunLineItemProps,
  ) => {
    // When this row's tool is newly created (not matched to an existing
    // product), also auto-resolve any other draft row referencing the exact
    // same part number + tool name — they're the same tool the admin just
    // created, so they shouldn't need a separate manual resolve.
    const newProductName = override.createProduct?.name;
    const matchesSameTool = (row: ToolsRunLineItemProps) =>
      row.rowIndex !== override.rowIndex &&
      row.status === "needs_review" &&
      !row.productId &&
      norm(row.partNumber) === norm(updatedRow.partNumber) &&
      norm(row.toolNameRaw) === norm(updatedRow.toolNameRaw);
    const cascadeRows = newProductName
      ? (draft?.rows || []).filter(matchesSameTool)
      : [];

    setOverrides((current) => {
      const next = current.filter(
        (item) =>
          item.rowIndex !== override.rowIndex &&
          !cascadeRows.some((row) => row.rowIndex === item.rowIndex),
      );
      next.push(override);
      cascadeRows.forEach((row) => {
        if (row.rowIndex == null) return;
        next.push({ rowIndex: row.rowIndex, createProduct: { name: newProductName as string } });
      });
      return next;
    });
    setDraft((current) =>
      current
        ? {
            ...current,
            rows: current.rows.map((row) => {
              if (row.rowIndex === updatedRow.rowIndex) return updatedRow;
              if (cascadeRows.some((cascaded) => cascaded.rowIndex === row.rowIndex)) {
                return {
                  ...row,
                  product: {
                    id: -1,
                    name: newProductName as string,
                    partNumber: row.partNumber || "",
                  },
                  status: "matched",
                  reviewReason: null,
                  suggestedProductIds: null,
                };
              }
              return row;
            }),
          }
        : current,
    );
    setEditingRow(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={cancel}
        fullWidth
        maxWidth={draft ? "xl" : "md"}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {draft ? "Review Tools Tracker draft" : "Import a Tools Tracker workbook"}
        </DialogTitle>
        <DialogContent>
          {busy ? (
            <LinearProgress
              sx={{ mb: 2, borderRadius: 2 }}
              color={busy === "confirm" ? "success" : "primary"}
            />
          ) : null}

          {!draft ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2.5 }}
              >
                Choose the client that owns this workbook before previewing it.
                Nothing is saved until you review and confirm the draft.
              </Typography>
              <Autocomplete
                options={clients}
                value={client}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => chooseClient(value)}
                disabled={Boolean(busy)}
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Workbook client"
                    placeholder="Search clients"
                    helperText="All rows in this file will be reconciled to this client."
                    sx={{ mb: 2.5 }}
                  />
                )}
              />
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2.5,
                  cursor: busy ? "default" : "pointer",
                  border: "1.5px dashed #C9C7BE",
                  borderRadius: 2.5,
                  bgcolor: "#FAFAF7",
                  transition: "border-color .2s, background .2s",
                  "&:hover": { borderColor: "#ED8B00", bgcolor: "#FFF9EE" },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#FFF0D5",
                    color: "#B86700",
                  }}
                >
                  {file ? (
                    <InsertDriveFileOutlinedIcon />
                  ) : (
                    <CloudUploadRoundedIcon />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={750} noWrap>
                    {file?.name || "Choose an Excel file"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    .xlsx only · up to 50 MB
                  </Typography>
                </Box>
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={selectFile}
                  disabled={Boolean(busy)}
                />
              </Box>
            </>
          ) : (
            <>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                sx={{ mb: 2.5 }}
              >
                {[
                  ["Rows", summary?.total],
                  ["Matched", summary?.matched],
                  ["Review", summary?.needsReview],
                  ["Errors", summary?.error],
                  ["Skipped", summary?.skippedDuplicate],
                ].map(([label, value], index) => (
                  <Paper
                    key={String(label)}
                    variant="outlined"
                    sx={{ flex: 1, p: 1.5, bgcolor: "#FAFAF7" }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ color: metricColors[index], fontWeight: 850 }}
                    >
                      {value}
                    </Typography>
                  </Paper>
                ))}
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1.25,
                }}
              >
                <Box>
                  <Typography fontWeight={800}>{client?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Review uncertain matches or edit any row before confirming.
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={statusFilter}
                      displayEmpty
                      inputProps={{ "aria-label": "Filter by status" }}
                      onChange={(event) =>
                        setStatusFilter(
                          event.target.value as typeof statusFilter,
                        )
                      }
                      sx={{ fontSize: 13, fontWeight: 700 }}
                    >
                      <MenuItem value="">All rows</MenuItem>
                      <MenuItem value="matched">Matched</MenuItem>
                      <MenuItem value="needs_review">Needs review</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                      <MenuItem value="skipped">Skipped</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    color="inherit"
                    onClick={() => {
                      setDraft(null);
                      setOverrides([]);
                      setStatusFilter("");
                    }}
                    disabled={Boolean(busy)}
                  >
                    Change file or client
                  </Button>
                </Stack>
              </Box>

              <TableContainer
                sx={{
                  maxHeight: "55vh",
                  border: "1px solid #E8E6DE",
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date / invoice</TableCell>
                      <TableCell>Source well</TableCell>
                      <TableCell>Resolved well</TableCell>
                      <TableCell>Tool</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!visibleRows.length ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {emptyStateMessage}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {visibleRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={750}>
                            {row.rawDate || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.invoice || "No invoice"}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.rawShipToWell || "—"}</TableCell>
                        <TableCell>
                          {row.well?.name ||
                            wells.find(
                              (well) =>
                                well.id ===
                                (row.wellId || row.suggestedWellId),
                            )?.name ||
                            "Not matched"}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {row.product?.name || row.toolNameRaw || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.partNumber || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.qty ?? row.qtyRaw}</TableCell>
                        <TableCell>
                          <ToolsRunStatusBadge status={row.status} />
                          {row.status !== "matched" ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.5,
                                textTransform: "capitalize",
                                maxWidth: 190,
                              }}
                            >
                              {reasonLabel(row.errorReason || row.reviewReason)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditRoundedIcon />}
                            disabled={row.status === "error" || Boolean(busy)}
                            onClick={() => setEditingRow(row)}
                          >
                            Edit match
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {summary?.error ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {summary.error} row(s) have invalid source data and will
                    be skipped — they will not be imported.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {errorReasonBreakdown
                      .map(
                        ([reason, count]) => `${reasonLabel(reason)} (${count})`,
                      )
                      .join(" · ")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Correct these in the workbook and re-upload if you need
                    them tracked.
                  </Typography>
                </Alert>
              ) : null}
            </>
          )}

          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={cancel} color="inherit">
            {busy ? "Cancel process" : "Cancel"}
          </Button>
          {!draft ? (
            <Button
              variant="contained"
              onClick={preview}
              disabled={!file || !client || Boolean(busy)}
              startIcon={<CloudUploadRoundedIcon />}
              sx={{ bgcolor: "#ED8B00", "&:hover": { bgcolor: "#C97500" } }}
            >
              Preview workbook
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={confirm}
              disabled={Boolean(busy)}
            >
              Confirm import
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ToolsRunDraftResolveDialog
        row={editingRow}
        clientName={client?.name || ""}
        wells={wells}
        products={products}
        onClose={() => setEditingRow(null)}
        onSave={saveDraftRow}
      />
    </>
  );
};
